import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Box, Button, Skeleton, Alert, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useFinancialFlows } from '../context'
import { ReportTypeItem } from 'src/admin/core/services/financial-flows'

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}))

const TabsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const ScrollContainer = styled(Box)({
  display: 'flex',
  overflow: 'hidden',
  flex: 1,
  position: 'relative',
})

const TabsInner = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  transition: 'transform 0.3s ease',
}))

const TabButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  textTransform: 'uppercase',
  minWidth: 'auto',
  padding: theme.spacing(1.5, 2),
  borderRadius: 0,
  fontWeight: 500,
  fontSize: '0.875rem',
  whiteSpace: 'nowrap',
  color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: isSelected ? theme.palette.primary.light + '15' : 'transparent',
  borderBottom: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const ScrollButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 0,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-disabled': {
    opacity: 0.3,
  },
}))

interface ReportTypeTabsProps {
  onReportTypeChange?: (reportType: ReportTypeItem) => void
}

export const ReportTypeTabs: React.FC<ReportTypeTabsProps> = ({ onReportTypeChange }) => {
  const { reportTypes, selectedReportType, reportTypesLoading, reportTypesError, loadReportTypes, setSelectedReportType, setReportData } =
    useFinancialFlows()

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const tabsInnerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  useEffect(() => {
    loadReportTypes()
  }, [])

  const checkScroll = useCallback(() => {
    const container = scrollContainerRef.current
    const inner = tabsInnerRef.current
    if (!container || !inner) return

    const containerWidth = container.offsetWidth
    const innerWidth = inner.scrollWidth
    const scrollLeft = container.scrollLeft

    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft + containerWidth < innerWidth - 1)
  }, [])

  useEffect(() => {
    checkScroll()
    
    // Use ResizeObserver to detect container size changes
    const container = scrollContainerRef.current
    if (container) {
      const resizeObserver = new ResizeObserver(() => {
        checkScroll()
      })
      resizeObserver.observe(container)
      
      return () => {
        resizeObserver.disconnect()
      }
    }
    
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [checkScroll, reportTypes])

  const handleScroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = 200
    const newScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    })

    setTimeout(checkScroll, 350)
  }

  const handleTabClick = (reportType: ReportTypeItem) => {
    setSelectedReportType(reportType)
    setReportData([]) // Clear previous search results when changing report type
    onReportTypeChange?.(reportType)
  }

  const selectedIndex = selectedReportType ? reportTypes.findIndex((r) => r.Name === selectedReportType.Name) : 0

  if (reportTypesLoading) {
    return (
      <LoadingContainer>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" width={140} height={40} sx={{ borderRadius: 1 }} />
        ))}
      </LoadingContainer>
    )
  }

  if (reportTypesError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {reportTypesError}
      </Alert>
    )
  }

  if (reportTypes.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No hay tipos de reporte disponibles
      </Alert>
    )
  }

  return (
    <TabsWrapper>
      <ScrollButton
        onClick={() => handleScroll('left')}
        disabled={!canScrollLeft}
        size="small"
        aria-label="scroll left"
      >
        <ChevronLeftIcon />
      </ScrollButton>

      <ScrollContainer
        ref={scrollContainerRef}
        onScroll={checkScroll}
        sx={{ overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        <TabsInner ref={tabsInnerRef}>
          {reportTypes.map((reportType, index) => (
            <TabButton
              key={reportType.Name}
              isSelected={selectedIndex === index}
              onClick={() => handleTabClick(reportType)}
              disableRipple={false}
            >
              {reportType.Description}
            </TabButton>
          ))}
        </TabsInner>
      </ScrollContainer>

      <ScrollButton
        onClick={() => handleScroll('right')}
        disabled={!canScrollRight}
        size="small"
        aria-label="scroll right"
      >
        <ChevronRightIcon />
      </ScrollButton>
    </TabsWrapper>
  )
}

export default ReportTypeTabs
